<!--
  StoreManagers.vue -- store-editor Team card (real-account provisioning)

  Card: Store-editor Team card role change -- https://trello.com/c/8wGst90i
  Design: docs/planning/2026-07-09-store-team-card-role-provisioning-design.md

  Reworked from the legacy bare-JSON shorthand. The card now:

    * offers CLIENT ADMIN / STORE MANAGER as role options (Shopper
      removed -- shoppers self-register / are invited to buy at the
      storefront, not seeded via distributor team setup);
    * PROVISIONS A REAL ACCOUNT when a person is added --
      POST /api/store/<id>/team creates an inactive Account (role by
      slug) + account_stores link + invite email, reusing the shipped
      create_distributor_with_owner / signUpSalesRep pipeline. It no
      longer appends to the ``stores.managers`` JSON column;
    * sources the roster from REAL accounts + pending invites
      (GET /api/store/<id>/team), NOT from ``campaign.managers``.

  The invitee accepts via the magic link (checkSignUpToken ->
  AddCredentials -> signUpSalesRep), activates, and lands on /portal
  (capability-driven postSignupRoute).

  NOTE: the ``stores.managers`` JSON column is INTENTIONALLY kept -- it
  still feeds the shopper-facing checkout "manager" dropdown (design
  Section 5). This card no longer WRITES it, but does not drop it.

  data-testid contract preserved from PR C4:
    team-invite-first-name/last-name/email/role/submit,
    team-member-row/avatar/name/email/role-chip/actions-trigger.
-->
<template>
  <EditorCard
    title="Team"
    description="Invite the people who manage this store. They'll get an email to set up their account."
  >
    <!-- Inline invite form -->
    <v-form class="team-invite-form" ref="form" @submit.prevent="addManager">
      <div class="invite-row">
        <v-text-field
          label="First name"
          v-model="inviteFirstName"
          :rules="[validateNoSpecials]"
          :disabled="$props.campaign.complete || submitting"
          data-testid="team-invite-first-name"
          density="compact"
          variant="outlined"
          hide-details="auto"
        />
        <v-text-field
          label="Last name"
          v-model="inviteLastName"
          :rules="[validateNoSpecials]"
          :disabled="$props.campaign.complete || submitting"
          data-testid="team-invite-last-name"
          density="compact"
          variant="outlined"
          hide-details="auto"
        />
        <v-text-field
          label="Work email"
          v-model="inviteEmail"
          :rules="[validateEmail]"
          :disabled="$props.campaign.complete || submitting"
          data-testid="team-invite-email"
          density="compact"
          variant="outlined"
          hide-details="auto"
        />
        <v-select
          label="Role"
          v-model="inviteRole"
          :items="roleOptions"
          :disabled="$props.campaign.complete || submitting"
          data-testid="team-invite-role"
          density="compact"
          variant="outlined"
          hide-details="auto"
        />
        <v-btn
          type="submit"
          :disabled="$props.campaign.complete || submitting"
          :loading="submitting"
          color="primary"
          data-testid="team-invite-submit"
          rounded="pill"
          class="invite-row__submit"
        >
          Invite
        </v-btn>
      </div>
    </v-form>

    <!-- Team member rows (real accounts + pending invites) -->
    <ul class="team-list" v-if="members.length">
      <li
        class="team-member"
        v-for="(member, index) in members"
        :key="member.email || index"
        data-testid="team-member-row"
      >
        <div
          class="team-member__avatar"
          :style="{ backgroundColor: avatarColor(member) }"
          data-testid="team-member-avatar"
        >
          {{ initialsFor(member) }}
        </div>
        <div class="team-member__info">
          <div class="team-member__name" data-testid="team-member-name">
            {{ member.name }}
          </div>
          <div class="team-member__email" data-testid="team-member-email">
            {{ member.email }}
          </div>
        </div>
        <span
          class="team-member__role-chip"
          :class="`team-member__role-chip--${roleClassFor(member)}`"
          data-testid="team-member-role-chip"
        >
          {{ roleLabelFor(member) }}
        </span>
        <button
          type="button"
          class="team-member__actions"
          :disabled="$props.campaign.complete"
          @click="onActionsClick(index)"
          data-testid="team-member-actions-trigger"
          :aria-label="`Actions for ${member.name}`"
        >
          <v-icon icon="more_horiz" />
        </button>
      </li>
    </ul>
  </EditorCard>
</template>

<script>
import rules from "@/mixins/rules";
import EditorCard from "./components/EditorCard.vue";

// The role options the distributor may provision from this card. Keyed by
// SLUG (ids drift across DBs); the backend resolves the Role by slug and
// hard-limits provisioning to this exact allowlist. Shopper is removed
// (design Section 3).
const ROLE_OPTIONS = [
  { title: "Client Admin", value: "client_admin" },
  { title: "Store Manager", value: "store_manager" },
];

const ROLE_LABELS = {
  client_admin: "Client Admin",
  store_manager: "Store Manager",
};

const DEFAULT_AVATAR_COLOR = "#5b6ee1";
const PENDING_AVATAR_COLOR = "#94a3b8";

function _initial(s) {
  return (s || "").trim().charAt(0).toUpperCase();
}

export default {
  components: {
    EditorCard,
  },

  props: {
    campaign: Object,
    editMode: Boolean,
  },

  mixins: [rules],

  data() {
    return {
      // Roster is sourced from REAL accounts + pending invites via the
      // store-team endpoint, NOT from campaign.managers.
      members: [],
      inviteFirstName: "",
      inviteLastName: "",
      inviteEmail: "",
      inviteRole: "client_admin",
      roleOptions: ROLE_OPTIONS,
      submitting: false,
    };
  },

  validate() {
    // Team members are now optional invites (provisioning is fire-and-
    // forget); adding one is not required to complete the tab.
    return true;
  },

  watch: {
    campaign: {
      immediate: true,
      handler(newVal) {
        if (this.editMode && !this._hasInitialized && newVal?._jv?.id) {
          this._hasInitialized = true;
          this.fillPreliminaryData();
        }
      },
    },
  },

  computed: {
    storeId() {
      const c = this.$props.campaign;
      return c && (c._jv?.id || c.id);
    },
  },

  methods: {
    validateTab() {
      // No blocking validation -- the team invite is optional.
      return false;
    },

    /**
     * Populate the card's preliminary data -- here, the team roster from
     * the store-team endpoint (real accounts + pending invites). Named
     * ``fillPreliminaryData`` for consistency with the other store-editor
     * tabs' campaign-watcher hydration pattern (the race-condition guard in
     * fillpreliminary-race-condition.spec asserts this shape). Silent on
     * failure -- a roster fetch error should not break the editor; the row
     * list simply stays as-is.
     */
    async fillPreliminaryData() {
      const id = this.storeId;
      if (!id) return;
      try {
        const resp = await this.$axios.get(`/store/${id}/team`);
        this.members = Array.isArray(resp.data) ? resp.data : [];
      } catch (e) {
        // Non-fatal: leave the current roster in place.
      }
    },

    roleClassFor(member) {
      // Pending invites (no accepted account yet) render in the muted
      // "pending" tone; active members use their role class.
      if (member.status === "pending") return "pending";
      return member.role === "store_manager" ? "store-manager" : "client-admin";
    },

    roleLabelFor(member) {
      if (member.status === "pending") {
        // A pending invite may not carry a role yet (legacy invite rows);
        // fall back to a generic "Invited" label.
        return member.role_label || ROLE_LABELS[member.role] || "Invited";
      }
      return member.role_label || ROLE_LABELS[member.role] || member.role || "";
    },

    initialsFor(member) {
      const name = (member.name || "").trim();
      if (!name) return _initial(member.email);
      const parts = name.split(/\s+/);
      if (parts.length === 1) return _initial(parts[0]);
      return `${_initial(parts[0])}${_initial(parts[parts.length - 1])}`;
    },

    avatarColor(member) {
      if (member.status === "pending") return PENDING_AVATAR_COLOR;
      const brand =
        this.$props.campaign?.distributor?.default_brand_color ||
        this.$props.campaign?.brand_color;
      return brand || DEFAULT_AVATAR_COLOR;
    },

    /**
     * Provision a real Client Admin / Store Manager account for this store.
     * POSTs to the store-team endpoint (which creates an inactive Account +
     * account_stores link + invite email); on success, refresh the roster.
     * The endpoint derives the tenant server-side -- no distributor_id is
     * sent from the client.
     */
    async addManager() {
      const f = (this.inviteFirstName || "").trim();
      const l = (this.inviteLastName || "").trim();
      const e = (this.inviteEmail || "").trim();
      if (!f || !l || !e) {
        this.$store.dispatch("raiseError", "Team member info cannot be blank");
        return;
      }
      const id = this.storeId;
      if (!id) {
        this.$store.dispatch(
          "raiseError",
          "Save the store before inviting team members"
        );
        return;
      }
      this.submitting = true;
      try {
        await this.$axios.post(`/store/${id}/team`, {
          first_name: f,
          last_name: l,
          email: e,
          role_slug: this.inviteRole || "client_admin",
        });
        this.inviteFirstName = "";
        this.inviteLastName = "";
        this.inviteEmail = "";
        this.inviteRole = "client_admin";
        await this.fillPreliminaryData();
        this.$emit("enableTabsBehind");
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      } finally {
        this.submitting = false;
      }
    },

    onActionsClick(index) {
      // Action drawer / menu wiring is feature-gated; the trigger emits a
      // UI event the parent shell picks up (unchanged from PR C4).
      this.$emit("team-member-actions", index);
    },
  },
};
</script>

<style scoped>
.team-invite-form {
  margin-bottom: 1.5rem;
  container-type: inline-size;
}

.invite-row {
  display: grid;
  /* Role gets a dedicated track wide enough for "Store Manager" plus the
     select arrow; the name/email tracks shrink (minmax 0) around it. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) 10rem auto;
  gap: 0.75rem;
  /* start, not center: validation messages grow a field downward and must
     not drag the button off the input baseline. */
  align-items: start;
}

/* Match the 40px compact outlined field height. */
.invite-row__submit {
  height: 40px;
}

/* The card sits beside two nav rails, so the usable width tracks the card,
   not the viewport: wrap on the form's own width (container query). */
/* Mid widths (the common case beside both rails at 1280px): the three text
   fields on the first row, Role + Invite on the second. DOM order is kept so
   tab order matches reading order. */
@container (max-width: 46rem) {
  .invite-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .invite-row__submit {
    justify-self: start;
  }
}

/* Narrow: two columns; email takes the full row. */
@container (max-width: 30rem) {
  .invite-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invite-row > [data-testid="team-invite-email"] {
    grid-column: 1 / -1;
  }
}

@container (max-width: 22rem) {
  .invite-row {
    grid-template-columns: 1fr;
  }
}

.team-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.team-member {
  display: grid;
  grid-template-columns: 40px 1fr auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--tf-bg);
  /* Slate-toned row border. The slate ramp lives in _tokens.css but
     the existing #e2e8f0 is between --tf-slate-divider and
     --tf-slate-border; --tf-slate-divider is the closer match for a
     subtle row divider. */
  border: 1px solid var(--tf-slate-divider);
  border-radius: var(--tf-radius-md);
}

.team-member__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-on-primary);
  font-weight: 600;
  font-size: 0.875rem;
}

.team-member__info {
  min-width: 0;
}

.team-member__name {
  font-weight: 600;
  /* Slate-ink for the headline name. */
  color: var(--tf-slate-ink);
}

.team-member__email {
  font-size: 0.875rem;
  /* Slate-body for the secondary line. */
  color: var(--tf-slate-body);
}

.team-member__role-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.125rem 0.5rem;
  border-radius: var(--tf-radius-pill);
  font-weight: 600;
}

.team-member__role-chip--client-admin {
  background: var(--tf-chip-manager-bg);
  color: var(--tf-chip-manager-fg);
}

.team-member__role-chip--store-manager {
  background: var(--tf-chip-manager-bg);
  color: var(--tf-chip-manager-fg);
}

.team-member__role-chip--pending {
  background: var(--tf-chip-shopper-bg);
  color: var(--tf-chip-shopper-fg);
}

.team-member__actions {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.team-member__actions:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.team-member__actions:hover:not(:disabled) {
  background: var(--tf-surface-alt);
}
</style>
